import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  BackupPaymentMethod,
  VXLIconResultTypenames,
} from '@varo/graphql-schema-types';
import React, { forwardRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

interface BackUpPaymentsBottomSheetProps {
  paymentOptions: BackupPaymentMethod[];
  testID?: string;
  analyticsObj: {
    id: string;
    customAttributes?: Record<string, string> | null;
  };
}
const BackUpPaymentsBottomSheet = (
  { paymentOptions, testID, analyticsObj }: BackUpPaymentsBottomSheetProps,
  ref: RefObject<BottomSheetModal>,
) => {
  const analytics = usePressableAnalytics({
    analyticsId: analyticsObj.id,
    customAttributes: analyticsObj.customAttributes,
  });

  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'review',
  });

  return (
    <BottomSheet
      ref={ref}
      accessibilityLabel="Backup Payment Method Bottom Sheet"
    >
      <ScrollView testID={testID}>
        <View style={styles.spacing}>
          <TitleMdText style={styles.title}>
            {translatedText('bottomSheet.paymentMethods.title')}
          </TitleMdText>
          <BodyBaseText>
            {translatedText('bottomSheet.paymentMethods.body')}
          </BodyBaseText>
        </View>

        {paymentOptions?.map((account, i) => {
          const title =
            `${account.name} ${account.placeholder ? '' : `• ${account.mask}`}`.trim();
          return (
            <View key={account?.name}>
              <ListItem
                primaryCopy={title}
                secondaryCopy={account.detail}
                showsChevron={false}
                leadingAccessory={{
                  id: account.name,
                  name: VXLComponentId.ICON_AVATAR,
                  props: {
                    variant: 'MD',
                    iconKey:
                      account.icon?.__typename ===
                      VXLIconResultTypenames.VXLIconType
                        ? account.icon.key
                        : undefined,
                    imageSource: {
                      uri:
                        account.icon?.__typename ===
                        VXLIconResultTypenames.URLType
                          ? account.icon.url
                          : undefined,
                    },
                  },
                }}
              />
              {/* Don't show Divider after last item */}
              {i !== paymentOptions.length - 1 && (
                <View style={styles.divider}>
                  <HairLineDivider />
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>
      <View style={styles.spacing}>
        <Button
          label={translatedText('buttons.gotIt')}
          onPress={() => {
            ref.current?.close();
            analytics();
          }}
        />
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  divider: {
    paddingHorizontal: theme.spacing.m,
  },
  spacing: {
    padding: theme.spacing.m,
  },
  title: {
    marginBottom: theme.spacing.s,
  },
});

export default forwardRef(BackUpPaymentsBottomSheet);
